import { useRef, useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCcpAcknowledgement } from "@/hooks/recipes/useCcpAcknowledgement";

export interface CcpStepFlagProps {
  /** The recipe the step belongs to, for the acknowledgement. Without it "Got it" only closes. */
  recipeId?: number;
  /** The cooking session the viewer was opened from, when it was. */
  cookingSessionId?: number;
  /** The step's own number, as the views and the stages know it: what the acknowledgement names. */
  number: number;
  /** The number the view shows for the step, where it counts places rather than the step's own number. */
  displayNumber?: number;
  /** The step's instruction: what the popup shows. */
  text: string;
  /** The stage the step sits in ("Render"), where the view knows it. */
  stageName?: string;
  className?: string;
}

/**
 * The red warning icon on a critical-control-point step, and the popup it
 * opens: "Critical control point", the step and its instruction, and a "Got it"
 * that records the cook has read it. The flag is only a flag — it carries no
 * limit and no corrective action, and who set it is not shown here.
 *
 * Hidden on paper, where the step prints `CcpStepPrintNote` instead.
 */
export function CcpStepFlag({ recipeId, cookingSessionId, number, displayNumber, text, stageName, className }: CcpStepFlagProps) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  // Inside a modal dialog (cook mode, where no app shell is around it) the popup
  // has to be portalled into that dialog, or the dialog's focus trap leaves it
  // unreachable. Elsewhere it goes to the body, as every popover does.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const acknowledge = useCcpAcknowledgement();

  const shown = displayNumber ?? number;
  const heading = stageName ? t`Step ${shown} · ${stageName}` : t`Step ${shown}`;

  const gotIt = () => {
    // Closed at once: a cook with wet hands should not wait on the network. A
    // failure is said, so the warning is reopened and acknowledged again.
    setOpen(false);
    if (recipeId == null) return;
    acknowledge.mutate(
      { recipeId, step: number, cookingSessionId },
      { onError: () => toast.error(t`Couldn't record that you read the warning on step ${shown}. Open it and tap Got it again.`) },
    );
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setContainer(triggerRef.current?.closest<HTMLElement>('[role="dialog"]') ?? null);
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          aria-label={t`Critical control point on step ${shown}: show warning`}
          className={cn(
            "inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-(--color-danger-bg) text-(--color-danger-text) ring-1 ring-(--color-danger-text)/40 transition-colors hover:bg-(--color-danger-text) hover:text-(--color-danger-bg) focus-visible:ring-2 focus-visible:ring-(--color-danger-text) focus-visible:outline-none print:hidden",
            className,
          )}
        >
          <TriangleAlert className="size-3.5" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        container={container}
        side="bottom"
        collisionPadding={16}
        className="flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col gap-3 rounded-2xl border-(--color-border) bg-(--color-surface-content) p-4 text-(--color-text-primary)"
      >
        <div className="flex flex-col gap-1">
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-(--color-danger-text)">
            <TriangleAlert className="size-4" aria-hidden />
            {t`Critical control point`}
          </span>
          <span className="text-xs font-semibold tracking-wide text-(--color-text-secondary) uppercase">{heading}</span>
        </div>
        <p className="text-sm leading-snug">{text || t`Untitled step`}</p>
        <Button type="button" size="sm" className="self-end" onClick={gotIt}>
          {t`Got it`}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
